/**
 * The lesson player — video on the left, "from learning to action" on the right.
 *
 * Replaces the mockup's storyboard with the real YouTube embed the super admin
 * curated. The quiz is graded server-side (POST /academy/lessons/{id}/quiz):
 * the answer key never travels with the lesson feed, so it cannot be read out
 * of the page.
 */
import { useEffect, useRef, useState } from "react";
import { Bookmark, BookmarkCheck, Check, CircleCheck, Clapperboard, Clock, X } from "lucide-react";

import { api, ApiError } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";
import {
  formatDuration,
  youtubeEmbedUrl,
  type AcademyLesson,
  type AcademyProgressState,
  type AcademyQuizResult,
} from "@/lib/academy";

import StoryboardPlayer from "./StoryboardPlayer";
import { useYouTubePlayer } from "./useYouTubePlayer";

export interface LessonDialogProps {
  lesson: AcademyLesson;
  onClose: () => void;
  /** Bubbles every progress change up so the page can re-render its counters. */
  onProgressChange: (lessonId: string, progress: AcademyProgressState) => void;
}

type DetailTab = "takeaways" | "transcript";

export default function LessonDialog({ lesson, onClose, onProgressChange }: LessonDialogProps) {
  const toast = useToast();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const progress = lesson.progress;
  const [detail, setDetail] = useState<DetailTab>("takeaways");
  const [note, setNote] = useState(progress?.action_note ?? "");
  const [savingNote, setSavingNote] = useState(false);
  const [answer, setAnswer] = useState<number | null>(progress?.quiz_answer_index ?? null);
  // Restored when the reader has answered this lesson before: the feed sends
  // the answer key back only once they have (see _to_response), so reopening a
  // lesson shows the result they already saw rather than a blank quiz with one
  // option mysteriously highlighted.
  const [quizResult, setQuizResult] = useState<AcademyQuizResult | null>(() =>
    progress?.quiz_answer_index != null && lesson.quiz_correct_index != null
      ? {
          is_correct: progress.quiz_answer_index === lesson.quiz_correct_index,
          correct_index: lesson.quiz_correct_index,
          explanation: lesson.quiz_explanation,
        }
      : null,
  );
  const [grading, setGrading] = useState(false);

  // Resume where they stopped, but not within the last 10 seconds — restarting
  // someone at 2:28 of a 2:30 lesson is worse than starting from the top.
  const resumeAt =
    progress && progress.last_position_seconds > 0 && !progress.is_completed
      ? lesson.duration_seconds > 0 && progress.last_position_seconds > lesson.duration_seconds - 10
        ? 0
        : progress.last_position_seconds
      : 0;

  // Built once per lesson: changing the src mid-lesson would reload the player
  // and throw away the viewer's position.
  const [embedSrc] = useState(() =>
    lesson.youtube_video_id ? youtubeEmbedUrl(lesson.youtube_video_id, { start: resumeAt }) : "",
  );

  const patchProgress = async (body: Record<string, unknown>) => {
    try {
      const updated = await api.patch<AcademyProgressState>(
        `/academy/lessons/${lesson.id}/progress`,
        body,
      );
      onProgressChange(lesson.id, updated);
      return updated;
    } catch (err) {
      // A failed resume ping must never interrupt the video, so only explicit
      // actions surface an error — those pass a toast-worthy body.
      if (!("last_position_seconds" in body)) {
        toast.error(err instanceof ApiError ? err.message : "Couldn't save that. Please try again.");
      }
      return null;
    }
  };

  useYouTubePlayer({
    iframeRef,
    videoId: lesson.youtube_video_id ?? "",
    onProgress: (seconds) => {
      void patchProgress({ last_position_seconds: seconds });
    },
    onEnded: () => {
      if (progress?.is_completed) return;
      void patchProgress({ is_completed: true }).then((updated) => {
        if (updated) toast.success("Lesson complete. Nice work.");
      });
    },
  });

  // Escape closes, matching every other dialog in the app.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // The page behind a full-screen dialog must not scroll with it.
  useEffect(() => {
    const prior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prior;
    };
  }, []);

  const toggleSaved = () => {
    void patchProgress({ is_saved: !progress?.is_saved });
  };

  const toggleComplete = () => {
    void patchProgress({ is_completed: !progress?.is_completed });
  };

  const submitAnswer = async (index: number) => {
    setAnswer(index);
    setGrading(true);
    try {
      const result = await api.post<AcademyQuizResult>(`/academy/lessons/${lesson.id}/quiz`, {
        quiz_answer_index: index,
      });
      setQuizResult(result);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't check that answer.");
    } finally {
      setGrading(false);
    }
  };

  const saveNote = async () => {
    const trimmed = note.trim();
    if (!trimmed) {
      toast.error("Write a specific question or next step first.");
      return;
    }
    setSavingNote(true);
    const updated = await patchProgress({ action_note: trimmed });
    setSavingNote(false);
    if (updated) toast.success("Saved to your buyer toolkit.");
  };

  const hasQuiz = !!lesson.quiz_question && lesson.quiz_answers.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="academy-lesson-title"
    >
      <div className="flex max-h-[94dvh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-5 py-3.5">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="text-[0.62rem] font-bold uppercase tracking-[0.16em] text-primary">
              BL Academy
            </span>
            <span className="hidden truncate text-xs text-muted-foreground sm:inline">
              / {lesson.stage_label}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close lesson"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 min-h-0 flex-1 overflow-y-auto lg:grid-cols-[1.55fr_1fr] lg:overflow-hidden">
          {/* ── Left: the video and its detail ── */}
          <div className="min-w-0 overflow-y-auto p-5 sm:p-6">
            <div className={cn("overflow-hidden rounded-xl border border-border", lesson.youtube_video_id ? "bg-black" : "bg-transparent")}>
              <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
                {lesson.youtube_video_id ? (
                  <iframe
                    ref={iframeRef}
                    src={embedSrc}
                    title={lesson.title}
                    className="absolute inset-0 h-full w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : lesson.storyboard.length > 0 ? (
                  // No video yet — play the storyboard, which is how the
                  // approved design teaches this lesson in the first place.
                  <div className="absolute inset-0">
                    <StoryboardPlayer
                      scenes={lesson.storyboard}
                      onFinished={() => {
                        if (progress?.is_completed) return;
                        void patchProgress({ is_completed: true });
                      }}
                    />
                  </div>
                ) : (
                  // Neither a video nor a storyboard. Say so plainly rather
                  // than rendering an empty frame.
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-muted/30 px-6 text-center">
                    <Clapperboard className="h-8 w-8 text-muted-foreground/70" />
                    <strong className="text-sm font-bold text-foreground">Coming soon</strong>
                    <span className="max-w-xs text-xs leading-6 text-muted-foreground">
                      This lesson is being produced. The key takeaways and your next step are ready
                      below.
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                {lesson.category && (
                  <div className="text-[0.62rem] font-bold uppercase tracking-[0.16em] text-primary">
                    {lesson.category}
                  </div>
                )}
                <h2
                  id="academy-lesson-title"
                  className="heading-bold mt-1.5 font-display text-[1.875rem] leading-tight text-foreground"
                >
                  {lesson.title}
                </h2>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    {formatDuration(lesson.duration_seconds)}
                  </span>
                  <span>{lesson.stage_label}</span>
                </div>
              </div>

              <div className="flex shrink-0 flex-wrap gap-2">
                <button
                  type="button"
                  onClick={toggleSaved}
                  aria-pressed={!!progress?.is_saved}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors",
                    progress?.is_saved
                      ? "border-primary/40 bg-primary/10 text-primary"
                      : "border-border bg-background text-foreground hover:border-primary/50 hover:text-primary",
                  )}
                >
                  {progress?.is_saved ? (
                    <BookmarkCheck className="h-3.5 w-3.5" />
                  ) : (
                    <Bookmark className="h-3.5 w-3.5" />
                  )}
                  {progress?.is_saved ? "Saved" : "Save lesson"}
                </button>
                <button
                  type="button"
                  onClick={toggleComplete}
                  aria-pressed={!!progress?.is_completed}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors",
                    progress?.is_completed
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600"
                      : "border-border bg-background text-foreground hover:border-primary/50 hover:text-primary",
                  )}
                >
                  <CircleCheck className="h-3.5 w-3.5" />
                  {progress?.is_completed ? "Completed" : "Mark complete"}
                </button>
              </div>
            </div>

            {lesson.summary && (
              <p className="mt-3 text-sm leading-7 text-muted-foreground">{lesson.summary}</p>
            )}

            {/* Takeaways / transcript. Only rendered when the author supplied
                at least one of them — an empty tab strip is worse than none. */}
            {(lesson.key_takeaways.length > 0 || lesson.transcript) && (
              <>
                <div className="mt-5 flex gap-1 border-b border-border">
                  {lesson.key_takeaways.length > 0 && (
                    <DetailTabButton
                      active={detail === "takeaways"}
                      onClick={() => setDetail("takeaways")}
                    >
                      Key takeaways
                    </DetailTabButton>
                  )}
                  {lesson.transcript && (
                    <DetailTabButton
                      active={detail === "transcript"}
                      onClick={() => setDetail("transcript")}
                    >
                      Transcript
                    </DetailTabButton>
                  )}
                </div>
                <div className="pt-4 text-sm leading-7 text-muted-foreground">
                  {detail === "takeaways" && lesson.key_takeaways.length > 0 ? (
                    <ul className="space-y-2.5">
                      {lesson.key_takeaways.map((item, i) => (
                        <li key={i} className="flex gap-2.5">
                          <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : lesson.transcript ? (
                    <p className="whitespace-pre-wrap">{lesson.transcript}</p>
                  ) : null}
                </div>
              </>
            )}
          </div>

          {/* ── Right: from learning to action ── */}
          <aside className="min-w-0 border-t border-border bg-muted/20 p-5 sm:p-6 lg:overflow-y-auto lg:border-l lg:border-t-0">
            <span className="text-[0.62rem] font-bold uppercase tracking-[0.16em] text-primary">
              From learning to action
            </span>
            <h3 className="heading-bold mt-2 font-display text-[1.2rem] text-foreground">Make this lesson count.</h3>

            {hasQuiz && (
              <div className="mt-5">
                <p className="text-sm font-semibold leading-6 text-foreground">
                  {lesson.quiz_question}
                </p>
                <div className="mt-3 space-y-2">
                  {lesson.quiz_answers.map((option, index) => {
                    const chosen = answer === index;
                    // Only colour answers once the server has graded — before
                    // that, nothing on the page knows which is right.
                    const graded = quizResult !== null;
                    const isRight = graded && index === quizResult.correct_index;
                    const isWrongChoice = graded && chosen && !quizResult.is_correct;
                    return (
                      <button
                        key={index}
                        type="button"
                        disabled={grading}
                        onClick={() => submitAnswer(index)}
                        className={cn(
                          "w-full rounded-xl border px-3.5 py-2.5 text-left text-sm transition-colors disabled:opacity-60",
                          isRight
                            ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-700"
                            : isWrongChoice
                              ? "border-rose-500/50 bg-rose-500/10 text-rose-700"
                              : chosen
                                ? "border-primary/50 bg-primary/10 text-foreground"
                                : "border-border bg-background text-foreground hover:border-primary/40",
                        )}
                      >
                        <span className="font-semibold">{String.fromCharCode(65 + index)}.</span>{" "}
                        {option}
                      </button>
                    );
                  })}
                </div>
                {quizResult && (
                  <p
                    aria-live="polite"
                    className={cn(
                      "mt-3 rounded-xl border px-3.5 py-2.5 text-xs leading-6",
                      quizResult.is_correct
                        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700"
                        : "border-amber-500/30 bg-amber-500/10 text-amber-700",
                    )}
                  >
                    {quizResult.is_correct ? "Correct. " : "Take another look. "}
                    {quizResult.explanation}
                  </p>
                )}
              </div>
            )}

            <div className="mt-6">
              <label
                htmlFor="academy-note"
                className="block text-sm font-semibold leading-6 text-foreground"
              >
                {lesson.action_prompt || "Your next step"}
              </label>
              <textarea
                id="academy-note"
                rows={4}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What would you want to verify?"
                className="mt-2 w-full resize-y rounded-xl border border-input bg-background px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
              <button
                type="button"
                onClick={saveNote}
                disabled={savingNote}
                className="mt-2.5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gold-gradient px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-[filter] hover:brightness-[1.06] disabled:opacity-60"
              >
                {savingNote ? "Saving…" : "Save to my buyer toolkit →"}
              </button>
            </div>

            <p className="mt-5 text-[0.7rem] leading-6 text-muted-foreground">
              Illustrative examples. Review deal-specific decisions with your accountant, lender,
              and attorney.
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
}

function DetailTabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "-mb-px border-b-2 px-3.5 py-2 text-sm font-semibold transition-colors",
        active
          ? "border-primary text-primary"
          : "border-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}
